import { useState, useCallback } from 'react';
import getStatistics from '../../../../services/getStatistics';
import { getCartSummaryStatistics } from '../../../../services/getCartSummaryStatistics';
import { getActiveCartsForStatistics } from '../../../../services/getActiveCartsForStatistics';
import getActiveUsersForStatistics from '../../../../services/getActiveUsersForStatistics';
import { getSiteOperatingHours, getOperatingHoursDisplay } from '../../../../services/getSiteOperatingHours';
import useAuth from '../../../../hooks/Auth/useAuth';

/**
 * Custom hook for managing all data fetching operations in the statistics dashboard
 */
const useStatisticsData = () => {
  const { relevantUrl } = useAuth();

  // Statistics data state
  const [statisticsData, setStatisticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState(false);

  // Cart summary data state
  const [cartSummaryData, setCartSummaryData] = useState(null);
  const [cartSummaryLoading, setCartSummaryLoading] = useState(false);

  // Carts and users data state
  const [cartsList, setCartsList] = useState([]);
  const [cartsLoading, setCartsLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Site operating hours state
  const [siteOperatingHours, setSiteOperatingHours] = useState(null);
  const [operatingHoursDisplay, setOperatingHoursDisplay] = useState('Loading...');

  // Fetch statistics data from backend
  const fetchStatisticsData = useCallback(async (
    dateRange,
    splitCount,
    sectionVisibility,
    filterStates,
    statsOpenHour,
    statsCloseHour
  ) => {
    setIsLoading(true);
    setStatisticsError(false);
    
    try {
      // Build statistics types array with their respective filters - only for enabled sections
      const statisticsTypes = [];
      
      if (sectionVisibility.demand) {
        statisticsTypes.push({
          type: 'demand',
          filterType: filterStates.demandFilterType,
          filters: filterStates.demandFilterType === 'specific' ? {
            specificUsers: filterStates.selectedUsers.length > 0 ? filterStates.selectedUsers.map(user => user.id) : []
          } : {
            userTypes: filterStates.selectedUserTypes,
            creationPeriod: filterStates.userCreationPeriod
          },
          isYAxisPercentage: filterStates.demandYAxisPercentage
        });
      }
      
      if (sectionVisibility.availability) {
        statisticsTypes.push({
          type: 'availability',
          filterType: filterStates.availabilityCartFilterType,
          filters: filterStates.availabilityCartFilterType === 'specific' ? {
            specificCarts: filterStates.selectedAvailabilityCarts.length > 0 ? filterStates.selectedAvailabilityCarts.map(cart => cart.id) : []
          } : {
            cartTypes: filterStates.selectedAvailabilityCartTypes
          },
          isYAxisPercentage: filterStates.availabilityYAxisPercentage
        });
      }
      
      if (sectionVisibility.onService) {
        statisticsTypes.push({
          type: 'onService',
          filterType: filterStates.onServiceCartFilterType,
          filters: filterStates.onServiceCartFilterType === 'specific' ? {
            specificCarts: filterStates.selectedOnServiceCarts.length > 0 ? filterStates.selectedOnServiceCarts.map(cart => cart.id) : []
          } : {
            cartTypes: filterStates.selectedOnServiceCartTypes
          },
          isYAxisPercentage: filterStates.onServiceYAxisPercentage
        });
      }
      
      if (sectionVisibility.waitingTime) {
        statisticsTypes.push({
          type: 'waitingTime',
          filterType: filterStates.waitingTimeCartFilterType,
          filters: filterStates.waitingTimeCartFilterType === 'specific' ? {
            specificCarts: filterStates.selectedWaitingTimeCarts.length > 0 ? filterStates.selectedWaitingTimeCarts.map(cart => cart.id) : []
          } : {
            cartTypes: filterStates.selectedWaitingTimeCartTypes
          },
          isYAxisPercentage: filterStates.waitingTimeYAxisPercentage
        });
      }
      
      if (sectionVisibility.trips) {
        statisticsTypes.push({
          type: 'trips',
          filterType: filterStates.tripCartFilterType,
          filters: {
            cartFilter: filterStates.tripCartFilterType === 'specific' ? {
              specificCarts: filterStates.selectedTripCarts.length > 0 ? filterStates.selectedTripCarts.map(cart => cart.id) : []
            } : {
              cartTypes: filterStates.selectedTripCartTypes
            },
            tripStatuses: filterStates.selectedTripStatuses,
            tripType: filterStates.tripType
          },
          isYAxisPercentage: filterStates.tripYAxisPercentage
        });
      }

      // Create generic request structure
      const requestData = {
        // Default parameters: time period and split count
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        timePeriods: splitCount,
        // Operating hours for full day interval calculation
        openHour: statsOpenHour,
        closeHour: statsCloseHour,
        // Array of statistics types with their configured filters
        statisticsTypes: statisticsTypes
      };

      console.log('Sending statistics request:', requestData);

      // Validate that we have some data to request
      if (statisticsTypes.length === 0) {
        console.warn('No statistics types enabled, skipping data fetch');
        setStatisticsData(null);
        setIsLoading(false);
        return;
      }

      // Use the service function to fetch data
      const data = await getStatistics(relevantUrl, requestData);
      
      if (data === 'error') {
        console.error('Failed to fetch statistics data from server');
        setStatisticsError(true);
        setStatisticsData(null);
      } else {
        console.log('Received statistics data:', data);
        setStatisticsData(data);
        setStatisticsError(false);
      }
    } catch (error) {
      console.error('Error fetching statistics data:', error);
      setStatisticsError(true);
      setStatisticsData(null);
    } finally {
      setIsLoading(false);
    }
  }, [relevantUrl]);

  // Fetch cart summary statistics
  const fetchCartSummaryData = useCallback(async (dateRange, cartFilter = 'all_active') => {
    setCartSummaryLoading(true);
    try {
      const data = await getCartSummaryStatistics(relevantUrl, dateRange[0], dateRange[1], cartFilter);
      
      if (data) {
        setCartSummaryData(data);
      } else {
        console.error('No cart summary data received from server');
        setCartSummaryData(null);
      }
    } catch (error) {
      console.error('Error fetching cart summary data:', error);
      setCartSummaryData(null);
    } finally {
      setCartSummaryLoading(false);
    }
  }, [relevantUrl]);

  // Fetch active and inactive carts for statistics filtering
  const fetchCartsData = useCallback(async () => {
    setCartsLoading(true);
    try {
      const response = await getActiveCartsForStatistics(relevantUrl);
      
      if (response.success && response.carts) {
        setCartsList(response.carts);
        console.log('Carts loaded successfully:', response.carts.length, 'carts');
        if (response.fromMock) {
          console.log('Using mock cart data');
        }
      } else {
        console.error('Failed to fetch carts data:', response.error);
      }
    } catch (error) {
      console.error('Error fetching carts data:', error);
    } finally {
      setCartsLoading(false);
    }
  }, [relevantUrl]);
  
  // Fetch active users for statistics filtering
  const fetchUsersData = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await getActiveUsersForStatistics(relevantUrl);
      
      if (response.success && response.users) {
        setUsersList(response.users);
        console.log('Users loaded successfully:', response.users.length, 'users');
        if (response.fromMock) {
          console.log('Using mock user data');
        }
      } else {
        console.error('Failed to fetch users data:', response.error);
      }
    } catch (error) {
      console.error('Error fetching users data:', error);
    } finally {
      setUsersLoading(false);
    }
  }, [relevantUrl]);

  // Fetch site operating hours
  const fetchSiteOperatingHours = useCallback(async () => {
    try {
      const hours = await getSiteOperatingHours(relevantUrl);
      if (hours) {
        setSiteOperatingHours(hours);
        const display = await getOperatingHoursDisplay(relevantUrl);
        setOperatingHoursDisplay(display);
        
        // Return parsed hours for initial setup
        const openHour = parseInt(hours.openHours.split(':')[0]);
        const closeHour = parseInt(hours.closeHours.split(':')[0]);
        
        console.log('Site operating hours loaded:', hours);
        console.log('Default stats hours set:', openHour, '-', closeHour);
        
        return { openHour, closeHour };
      }
    } catch (error) {
      console.error('Error fetching site operating hours:', error);
      setOperatingHoursDisplay('Operating hours unavailable');
      return null;
    }
  }, [relevantUrl]);

  // Fetch specific demand statistics
  const fetchDemandStatistics = useCallback(async (
    dateRange,
    splitCount,
    filterStates,
    statsOpenHour,
    statsCloseHour
  ) => {
    setIsLoading(true);
    setStatisticsError(false);
    
    try {
      // Build only demand statistics type
      const statisticsTypes = [{
        type: 'demand',
        filterType: filterStates.demandFilterType,
        filters: filterStates.demandFilterType === 'specific' ? {
          specificUsers: filterStates.selectedUsers.length > 0 ? filterStates.selectedUsers.map(user => user.id) : []
        } : {
          userTypes: filterStates.selectedUserTypes,
          creationPeriod: filterStates.userCreationPeriod
        },
        isYAxisPercentage: filterStates.demandYAxisPercentage
      }];

      // Create request structure
      const requestData = {
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        timePeriods: splitCount,
        openHour: statsOpenHour,
        closeHour: statsCloseHour,
        statisticsTypes: statisticsTypes
      };

      console.log('Sending demand statistics request:', requestData);

      // Use the service function to fetch data
      const data = await getStatistics(relevantUrl, requestData);
      
      if (data === 'error') {
        console.error('Failed to fetch demand statistics data');
        setStatisticsError(true);
      } else {
        console.log('Received demand statistics data:', data);
        
        // Merge with existing data or set as new data
        if (statisticsData) {
          // Create a new object with updated demand data
          const updatedData = { ...statisticsData };
          
          // Only update the demand property
          if (data.charts.demand) {
            updatedData.charts.demand = data.charts.demand;
          } else {
            console.warn('Statistics data does not contain demand property');
          }
          
          setStatisticsData(updatedData);
        } else {
          // If there's no existing data, create a new object with only the demand property
          const newData = { demand: data.demand };
          setStatisticsData(newData);
        }
        
        setStatisticsError(false);
      }
    } catch (error) {
      console.error('Error fetching demand statistics data:', error);
      setStatisticsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [relevantUrl, statisticsData]);

  // Fetch specific cart statistics (availability or onService)
  const fetchCartStatistics = useCallback(async (
    dateRange,
    splitCount,
    statisticsType, // 'availability' or 'onService'
    filterStates,
    statsOpenHour,
    statsCloseHour
  ) => {
    setIsLoading(true);
    setStatisticsError(false);
    
    try {
      // Determine which filter state to use based on statistics type
      let filterType, specificCarts, cartTypes;
      
      if (statisticsType === 'availability') {
        filterType = filterStates.availabilityCartFilterType;
        specificCarts = filterStates.selectedAvailabilityCarts;
        cartTypes = filterStates.selectedAvailabilityCartTypes;
      } else if (statisticsType === 'onService') {
        filterType = filterStates.onServiceCartFilterType;
        specificCarts = filterStates.selectedOnServiceCarts;
        cartTypes = filterStates.selectedOnServiceCartTypes;
      } else if (statisticsType === 'waitingTime') {
        filterType = filterStates.waitingTimeCartFilterType;
        specificCarts = filterStates.selectedWaitingTimeCarts;
        cartTypes = filterStates.selectedWaitingTimeCartTypes;
      }
      
      // Build statistics types array with cart-related filter
      const statisticsTypes = [{
        type: statisticsType,
        filterType: filterType,
        filters: filterType === 'specific' ? {
          specificCarts: specificCarts.length > 0 ? specificCarts.map(cart => cart.id) : []
        } : {
          cartTypes: cartTypes
        },
        isYAxisPercentage: statisticsType === 'availability' 
          ? filterStates.availabilityYAxisPercentage 
          : statisticsType === 'onService'
            ? filterStates.onServiceYAxisPercentage
            : filterStates.waitingTimeYAxisPercentage
      }];

      // Create request structure
      const requestData = {
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        timePeriods: splitCount,
        openHour: statsOpenHour,
        closeHour: statsCloseHour,
        statisticsTypes: statisticsTypes
      };

      console.log(`Sending ${statisticsType} statistics request:`, requestData);

      // Use the service function to fetch data
      const data = await getStatistics(relevantUrl, requestData);
      
      if (data === 'error') {
        console.error(`Failed to fetch ${statisticsType} statistics data`);
        setStatisticsError(true);
      } else {
        console.log(`Received ${statisticsType} statistics data:`, data);
        
        // Merge with existing data or set as new data
        if (statisticsData) {
          // Create a new object with updated data, but only update the specific statistics type
          const updatedData = { ...statisticsData };
          
          // Only update the specific statistics type property
          if (data.charts[statisticsType]) {
            updatedData.charts[statisticsType] = data.charts[statisticsType];
          } else {
            console.warn(`Statistics data does not contain ${statisticsType} property`);
          }
          
          setStatisticsData(updatedData);
        } else {
          // If there's no existing data, create a new object with only the specific statistics type
          const newData = { [statisticsType]: data[statisticsType] };
          setStatisticsData(newData);
        }
        
        setStatisticsError(false);
      }
    } catch (error) {
      console.error(`Error fetching ${statisticsType} statistics data:`, error);
      setStatisticsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [relevantUrl, statisticsData]);

  // Fetch specific trip statistics
  const fetchTripStatistics = useCallback(async (
    dateRange,
    splitCount,
    filterStates,
    statsOpenHour,
    statsCloseHour
  ) => {
    setIsLoading(true);
    setStatisticsError(false);
    
    try {
      // Build only trips statistics type
      const statisticsTypes = [{
        type: 'trips',
        filterType: filterStates.tripCartFilterType,
        filters: {
          cartFilter: filterStates.tripCartFilterType === 'specific' ? {
            specificCarts: filterStates.selectedTripCarts.length > 0 ? filterStates.selectedTripCarts.map(cart => cart.id) : []
          } : {
            cartTypes: filterStates.selectedTripCartTypes
          },
          tripStatuses: filterStates.selectedTripStatuses,
          tripType: filterStates.tripType
        },
        isYAxisPercentage: filterStates.tripYAxisPercentage
      }];

      // Create request structure
      const requestData = {
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        timePeriods: splitCount,
        openHour: statsOpenHour,
        closeHour: statsCloseHour,
        statisticsTypes: statisticsTypes
      };

      console.log('Sending trip statistics request:', requestData);

      // Use the service function to fetch data
      const data = await getStatistics(relevantUrl, requestData);
      
      if (data === 'error') {
        console.error('Failed to fetch trip statistics data');
        setStatisticsError(true);
      } else {
        console.log('Received trip statistics data:', data);
        
        // Merge with existing data or set as new data
        if (statisticsData) {
          // Create a new object with updated trips data
          const updatedData = { ...statisticsData };
          
          // Only update the trips property
          if (data.charts.trips) {
            updatedData.charts.trips = data.charts.trips;
          } else {
            console.warn('Statistics data does not contain trips property');
          }
          
          setStatisticsData(updatedData);
        } else {
          // If there's no existing data, create a new object with only the trips property
          const newData = { trips: data.charts.trips };
          setStatisticsData(newData);
        }
        
        setStatisticsError(false);
      }
    } catch (error) {
      console.error('Error fetching trip statistics data:', error);
      setStatisticsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [relevantUrl, statisticsData]);

  return {
    // Data state
    statisticsData,
    isLoading,
    statisticsError,
    cartSummaryData,
    cartSummaryLoading,
    cartsList,
    cartsLoading,
    usersList,
    usersLoading,
    siteOperatingHours,
    operatingHoursDisplay,
    
    // Fetch functions
    fetchStatisticsData,
    fetchCartSummaryData,
    fetchCartsData,
    fetchUsersData,
    fetchSiteOperatingHours,
    fetchDemandStatistics,
    fetchCartStatistics,
    fetchTripStatistics
  };
};

export default useStatisticsData;
